/**
 * Admin tools modal (admins only, `players.is_admin`): the Dice Lab button (`DiceLab`, motion dice
 * tuning and tests), right now (online, lobbies, games, uptime),
 * players, feedback and games over 24 h / 7 days / all time, the five newest reports and game logs,
 * each linking to its full page (`/admin/feedback?id=`, `/admin/games?id=`). Loaded fresh each time
 * it opens (`GET /api/admin/summary`).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { t, tn, type AdminSummary, type AdminWindows } from '@kadice/shared';
import { api } from '../api';

const WINDOWS = ['day', 'week', 'total'] as const;

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

function uptime(s: number): string {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return t('admin.uptime.dh', { d, h });
  if (h > 0) return t('admin.uptime.hm', { h, m });
  return t('admin.uptime.m', { m });
}

/** One row per key, one column per window. */
function CountTable<K extends string>({
  rows,
  counts,
}: {
  rows: readonly { key: K; label: string }[];
  counts: AdminWindows<Record<K, number>>;
}) {
  return (
    <table className="admin-counts">
      <thead>
        <tr>
          <th />
          {WINDOWS.map((w) => (
            <th key={w}>{t(`admin.window.${w}`)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <th>{r.label}</th>
            {WINDOWS.map((w) => (
              <td key={w}>{counts[w][r.key].toLocaleString()}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** A full report page, in a new tab. */
const Full = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} target="_blank" rel="noreferrer">
    {children}
  </a>
);

export function AdminTools({ onClose, onDiceLab }: { onClose: () => void; onDiceLab: () => void }) {
  const [data, setData] = useState<AdminSummary | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.adminSummary().then(setData, () => setFailed(true));
  }, []);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal admin-tools"
        role="dialog"
        aria-modal="true"
        aria-label={t('admin.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row">
          <h3>{t('admin.title')}</h3>
          <span className="spacer" />
          <button onClick={onDiceLab}>{t('admin.diceLab')}</button>
          <button onClick={onClose}>{t('common.close')}</button>
        </div>
        {failed ? (
          <p className="muted">{t('admin.failed')}</p>
        ) : !data ? (
          <p className="muted">{t('admin.loading')}</p>
        ) : (
          <Summary data={data} />
        )}
      </div>
    </div>
  );
}

function Summary({ data }: { data: AdminSummary }) {
  const { live, players, feedback, games } = data;
  const playerCounts = {
    day: { new: players.new.day, played: players.played.day },
    week: { new: players.new.week, played: players.played.week },
    total: { new: players.new.total, played: players.played.total },
  };
  return (
    <div className="admin-sections">
      <section>
        <h4>{t('admin.live')}</h4>
        <div className="admin-live">
          {(['online', 'lobbies', 'playing'] as const).map((k) => (
            <span key={k} className="admin-stat">
              <strong>{live[k]}</strong> {t(`admin.live.${k}`)}
            </span>
          ))}
          <span className="admin-stat">
            <strong>{uptime(live.uptimeS)}</strong> {t('admin.live.uptime')}
          </span>
        </div>
      </section>

      <section>
        <div className="row">
          <h4>{t('admin.players')}</h4>
          <span className="spacer" />
          <Full href="/admin/players">{t('admin.players.all')}</Full>
        </div>
        <CountTable
          rows={[
            { key: 'new', label: t('admin.players.new') },
            { key: 'played', label: t('admin.players.played') },
          ]}
          counts={playerCounts}
        />
      </section>

      <section>
        <div className="row">
          <h4>{t('admin.feedback')}</h4>
          <span className="spacer" />
          <Full href="/admin/feedback">{t('admin.feedback.all')}</Full>
        </div>
        <CountTable
          rows={(['bug', 'suggestion', 'other'] as const).map((key) => ({ key, label: t(`admin.feedback.${key}`) }))}
          counts={feedback.counts}
        />
        {feedback.recent.length === 0 ? (
          <p className="muted small">{t('admin.feedback.none')}</p>
        ) : (
          <ul className="admin-recent">
            {feedback.recent.map((r) => (
              <li key={r.id}>
                <Full href={`/admin/feedback?id=${r.id}`}>
                  <span className={`badge cat-${r.category}`}>{t(`admin.feedback.${r.category}`)}</span> {r.title}
                </Full>
                <span className="muted small">
                  {r.playerName} · {when(r.createdAt)}
                  {r.imageCount > 0 && ` · ${tn('admin.feedback.images', r.imageCount)}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <div className="row">
          <h4>{t('admin.games')}</h4>
          <span className="spacer" />
          <Full href="/admin/games">{t('admin.games.all')}</Full>
        </div>
        <CountTable
          rows={(['started', 'finished', 'abandoned', 'inProgress'] as const).map((key) => ({
            key,
            label: t(`admin.games.${key}`),
          }))}
          counts={games.counts}
        />
        {games.recent.length === 0 ? (
          <p className="muted small">{t('admin.games.none')}</p>
        ) : (
          <ul className="admin-recent">
            {games.recent.map((g) => (
              <li key={g.gameId}>
                <Full href={`/admin/games?id=${g.gameId}`}>
                  {t('admin.games.game', { id: g.gameId })} ·{' '}
                  {g.result === 'winner'
                    ? t('admin.games.won', { name: g.winnerName ?? '' })
                    : t(`admin.games.result.${g.result}`)}
                </Full>
                <span className="muted small">
                  {g.players.map((p) => p.name).join(', ')} · {when(g.startedAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
